import { useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { getPlayer } from "@/lib/sharedAudio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function computeEnvelope(audioBuffer) {
  const ch = audioBuffer.getChannelData(0);
  const sr = audioBuffer.sampleRate;
  // 20ms bins; normalised to the loud end of her speech (95th percentile, not one stray peak),
  // with a soft gate so breaths and room tone keep the lips closed.
  const binMs = 20;
  const binSize = Math.max(1, Math.floor((sr * binMs) / 1000));
  const bins = [];
  for (let i = 0; i < ch.length; i += binSize) {
    let sum = 0, n = 0;
    for (let j = i; j < i + binSize && j < ch.length; j++) { sum += ch[j] * ch[j]; n++; }
    bins.push(Math.sqrt(sum / Math.max(1, n)));
  }
  const sorted = bins.filter((b) => b > 1e-4).sort((x, y) => x - y);
  const ref = Math.max(1e-4, sorted[Math.floor(sorted.length * 0.95)] || 1e-4);
  const gate = 0.07;
  return { bins: bins.map((b) => { const v = Math.min(1, b / ref); return v < gate ? 0 : (v - gate) / (1 - gate); }), binMs };
}

// Lips move a fraction before the sound is heard (as real speech does).
export const LIP_LEAD_MS = 60;
export function envAt(env, seconds) {
  if (!env || !env.bins.length) { // rare decode failure: a calm ~4 syllables/sec rhythm with phrase pauses, never a flutter
    const t = seconds;
    const phrase = Math.sin(t * 0.9) + Math.sin(t * 2.1) * 0.5 > -0.6 ? 1 : 0;
    return phrase * Math.max(0, Math.sin(t * 26 + Math.sin(t * 3.1) * 2.2)) * 0.6;
  }
  return env.bins[Math.floor((seconds * 1000 + LIP_LEAD_MS) / env.binMs)] ?? 0;
}

// Speaks a line in the coach's voice and drives amplitudeRef from the real audio envelope.
export function useOrbVoice(voice = "sage", kind = "onboarding") {
  const amplitudeRef = useRef(0);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const rafRef = useRef(null);
  const tokenRef = useRef(0);

  const stop = useCallback(() => {
    tokenRef.current += 1;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { audioRef.current?.pause(); } catch {}
    amplitudeRef.current = 0;
  }, []);

  useEffect(() => () => {
    stop();
    try { if (ctxRef.current && ctxRef.current.state !== "closed") ctxRef.current.close().catch(() => {}); } catch {}
  }, [stop]);

  const speak = useCallback(async (text, presetUrl) => {
    stop();
    const my = tokenRef.current;
    if (!audioRef.current) audioRef.current = getPlayer();
    const srcAtStart = audioRef.current.src;
    if (!ctxRef.current) { try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch {} }
    try {
      const data = presetUrl ? { url: presetUrl } : (await api.post("/tts", { text, voice, premium: false, session_kind: kind })).data;
      if (!data.url || my !== tokenRef.current) return false;
      const buf = await (await fetch(`${BACKEND_URL}${data.url}`)).arrayBuffer();
      let env = null;
      try { env = computeEnvelope(await ctxRef.current.decodeAudioData(buf.slice(0))); } catch {}
      if (my !== tokenRef.current) return false;
      const el = audioRef.current;
      if (el.src !== srcAtStart && !el.paused) return false; // something else (e.g. a Reset he tapped) took over — don't cut it off
      const url = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
      el.src = url;
      await new Promise((resolve) => {
        const done = () => { amplitudeRef.current = 0; URL.revokeObjectURL(url); el.onended = null; resolve(); };
        el.onended = done; el.onerror = done;
        const drive = () => {
          if (el.paused || el.ended || my !== tokenRef.current) return;
          const a = env ? envAt(env, el.currentTime) : 0;
          amplitudeRef.current = Math.pow(Math.max(0, Math.min(1, a)), 0.7);
          rafRef.current = requestAnimationFrame(drive);
        };
        el.play().then(() => { rafRef.current = requestAnimationFrame(drive); }).catch(done);
      });
      return true;
    } catch { amplitudeRef.current = 0; return false; }
  }, [voice, kind, stop]);

  return { amplitudeRef, speak, stop };
}
